import { AsYouType, parsePhoneNumberFromString, validatePhoneNumberLength } from 'libphonenumber-js'
import metadata from 'libphonenumber-js/metadata.min.json'

export const DEFAULT_DDI = '55'

// Padrão E.164: DDI + número nacional têm no máximo 15 dígitos.
const MAX_DIGITS = 15

// DDI -> países que o usam; o primeiro é o principal (ex.: '1' -> ['US', 'CA', ...]).
const CALLING_CODES = metadata.country_calling_codes

function regionNames(locale) {
  try {
    return new Intl.DisplayNames([locale], { type: 'region' })
  } catch (e) {
    return null
  }
}

/** Opções do select de DDI: [{ value: '55', region: 'BR', text: '+55 · Brasil' }], em ordem numérica. */
export function ddiOptions(locale = 'pt-BR') {
  const names = regionNames(locale)
  const nameOf = region => (names && names.of(region)) || region

  return Object.keys(CALLING_CODES)
    .sort((a, b) => Number(a) - Number(b))
    .map(ddi => {
      const regions = CALLING_CODES[ddi]
      const suffix = regions.length > 1 ? '…' : ''
      return { value: ddi, region: regions[0], text: `+${ddi} · ${nameOf(regions[0])}${suffix}` }
    })
}

// Corta os dígitos que passam do tamanho máximo de número do país (Brasil: 11).
function clampDigits(ddi, value) {
  let digits = String(value || '')
    .replace(/\D/g, '')
    .slice(0, MAX_DIGITS - String(ddi).length)

  while (digits && validatePhoneNumberLength(`+${ddi}${digits}`) === 'TOO_LONG') {
    digits = digits.slice(0, -1)
  }

  return digits
}

// Tira o prefixo de tronco que o operador costuma digitar (ex.: o 0 de "011 99999-8888" no
// Brasil, o 1 nos EUA). Países em que o 0 faz parte do número (ex.: Itália) não são alterados.
function withoutTrunkPrefix(region, digits) {
  const typed = new AsYouType(region)
  typed.input(digits)
  const national = typed.getNationalNumber()

  return national && national !== digits ? national : digits
}

const hasMask = text => /\D/.test(text)

/**
 * Aplica a máscara nacional do país do DDI enquanto o operador digita.
 *
 * Alguns países têm o prefixo de tronco na máscara (França: "06 12 34 56 78") e outros não
 * (Brasil: "(11) 99999-8888", mesmo se digitarem "011..."). Tenta como foi digitado e, se não
 * ganhar máscara, tenta sem o prefixo.
 */
export function formatPhone(ddi, value) {
  const region = (CALLING_CODES[ddi] || [])[0]
  const typed = String(value || '').replace(/\D/g, '')

  if (!typed || !region) return clampDigits(ddi, typed)

  const asTyped = clampDigits(ddi, typed)
  const maskedAsTyped = new AsYouType(region).input(asTyped)
  if (hasMask(maskedAsTyped)) return maskedAsTyped

  const withoutPrefix = clampDigits(ddi, withoutTrunkPrefix(region, typed))
  const maskedWithoutPrefix = new AsYouType(region).input(withoutPrefix)

  return hasMask(maskedWithoutPrefix) ? maskedWithoutPrefix : asTyped
}

/**
 * Número digitado/colado já com DDI ("+55 11 99999-8888"): devolve { ddi, national }.
 * Devolve null enquanto o DDI ainda não dá pra identificar (ex.: "+5").
 */
export function splitInternational(value) {
  const typed = new AsYouType()
  typed.input(String(value || ''))

  const ddi = typed.getCallingCode()
  if (!ddi) return null

  const digits = String(value).replace(/\D/g, '')

  return { ddi, national: digits.slice(ddi.length) }
}

/** Número completo e válido para o país do DDI (substitui o antigo "pelo menos 10 dígitos", que só valia no Brasil). */
export function isCompletePhone(ddi, value) {
  const digits = String(value || '').replace(/\D/g, '')
  if (!digits || !ddi) return false

  const parsed = parsePhoneNumberFromString(`+${ddi}${digits}`)

  return !!parsed && parsed.isValid()
}

/** Telefone para exibição: "+55 (11) 99999-8888". Sem DDI gravado, devolve o telefone como está. */
export function phoneWithDdi(ddi, phone) {
  if (!phone) return phone

  return ddi ? `+${ddi} ${phone}` : phone
}
